/* eslint-disable no-unused-vars */
// CrossPortalIntegration.js
// Cross-portal communication system for Business and Professional portals
// With live API integration to api.jobeng.ai

import AsyncStorage from '@react-native-async-storage/async-storage';
import { Alert } from 'react-native';

const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const apiCall = async (method, path, body = null) => {
  try {
    const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
    const companyId = await AsyncStorage.getItem('company_id') || await AsyncStorage.getItem('company_id') || '1';
    const headers = { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': API_KEY };
    if (token) headers.Authorization = `Bearer ${token}`;
    const opts = { method, headers };
    const separator = path.includes('?') ? '&' : '?';
    const url = `${API_BASE_URL}${path}${separator}company_id=${companyId}`;
    if (body && method !== 'GET') opts.body = JSON.stringify({ ...body, company_id: parseInt(companyId) });
    const res = await fetch(url, opts);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch (e) { console.warn('API call failed:', e.message); return { success: false, data: [] }; }
};

export class CrossPortalDataManager {
  static instance = null;

  constructor() {
    if (CrossPortalDataManager.instance) return CrossPortalDataManager.instance;
    CrossPortalDataManager.instance = this;
  }

  static STORAGE_KEYS = {
    BUSINESS_JOBS: 'businessJobs', PROFESSIONAL_APPLICATIONS: 'professionalApplications',
    CROSS_PORTAL_SYNC: 'crossPortalSync', JOB_APPLICATIONS: 'jobApplications',
    BUSINESS_CANDIDATES: 'businessCandidates', PROFESSIONAL_PROFILE: 'professionalProfile',
    NOTIFICATION_QUEUE: 'notificationQueue', PORTAL_COMMUNICATIONS: 'portalCommunications',
  };

  async syncJobsToProPortal(businessJobs) {
    try {
      // Fetch live jobs from API
      const apiJobs = await apiCall('GET', '/api/jobs');
      const jobs = apiJobs?.success && Array.isArray(apiJobs.data) ? apiJobs.data : (businessJobs || []);
      const syncedJobs = jobs.map(job => ({
        ...job, id: job.id || `job_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        syncedAt: new Date().toISOString(), sourcePortal: 'business', targetPortal: 'professional',
        syncStatus: 'synced',
      }));
      await AsyncStorage.setItem(CrossPortalDataManager.STORAGE_KEYS.BUSINESS_JOBS, JSON.stringify(syncedJobs));
      await this.updateSyncStatus('jobs', 'success', new Date().toISOString());
      return { success: true, syncedCount: syncedJobs.length, jobs: syncedJobs };
    } catch (error) {
      await this.updateSyncStatus('jobs', 'failed', new Date().toISOString(), error.message);
      return { success: false, error: error.message };
    }
  }

  async syncApplicationsToBizPortal(applications) {
    try {
      const apiApps = await apiCall('GET', '/api/applications');
      const apps = apiApps?.success && Array.isArray(apiApps.data) ? apiApps.data : (applications || []);
      const syncedApps = apps.map(app => ({
        ...app, id: app.id || `app_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        syncedAt: new Date().toISOString(), sourcePortal: 'professional', targetPortal: 'business',
        syncStatus: 'synced',
      }));
      await AsyncStorage.setItem(CrossPortalDataManager.STORAGE_KEYS.JOB_APPLICATIONS, JSON.stringify(syncedApps));
      // Also update business candidates
      const candidates = syncedApps.map(app => ({
        applicationId: app.id, name: app.candidate_name || app.applicant_name || 'Unknown',
        email: app.email || '', status: app.status || 'new', stage: app.stage || 'applied',
        jobId: app.job_posting_id || app.job_id, syncedAt: new Date().toISOString(),
      }));
      await AsyncStorage.setItem(CrossPortalDataManager.STORAGE_KEYS.BUSINESS_CANDIDATES, JSON.stringify(candidates));
      await this.updateSyncStatus('applications', 'success', new Date().toISOString());
      return { success: true, syncedCount: syncedApps.length, applications: syncedApps };
    } catch (error) {
      await this.updateSyncStatus('applications', 'failed', new Date().toISOString(), error.message);
      return { success: false, error: error.message };
    }
  }

  async getBusinessCandidates() {
    try {
      // Try API first
      const apiApps = await apiCall('GET', '/api/applications');
      if (apiApps?.success && Array.isArray(apiApps.data) && apiApps.data.length > 0) {
        const apiJobs = await apiCall('GET', '/api/jobs');
        const jobs = apiJobs?.success && Array.isArray(apiJobs.data) ? apiJobs.data : [];
        return apiApps.data.map(app => ({
          id: app.id, applicationId: app.id, name: app.candidate_name || app.applicant_name || 'Unknown',
          email: app.email || '', phone: app.phone || '', status: app.status || 'new', stage: app.stage || 'applied',
          jobId: app.job_posting_id || app.job_id, jobTitle: app.job_title || jobs.find(j => String(j.id) === String(app.job_posting_id))?.title || '',
          appliedDate: app.applied_date || app.created_at, resumeUrl: app.resume_url || '',
          coverLetterUrl: app.cover_letter_url || '', notes: app.notes || '', rating: app.rating || 0,
          source: app.source || 'direct', syncedAt: new Date().toISOString(),
        }));
      }
      // Fallback to local storage
      const stored = await AsyncStorage.getItem(CrossPortalDataManager.STORAGE_KEYS.BUSINESS_CANDIDATES);
      return stored ? JSON.parse(stored) : [];
    } catch (error) { console.error('Error getting candidates:', error); return []; }
  }

  async updateSyncStatus(type, status, timestamp, error = null) {
    try {
      const stored = await AsyncStorage.getItem(CrossPortalDataManager.STORAGE_KEYS.CROSS_PORTAL_SYNC);
      const syncData = stored ? JSON.parse(stored) : {};
      syncData[type] = { status, timestamp, error, lastSync: new Date().toISOString() };
      await AsyncStorage.setItem(CrossPortalDataManager.STORAGE_KEYS.CROSS_PORTAL_SYNC, JSON.stringify(syncData));
    } catch (e) { console.error('Sync status update failed:', e); }
  }

  async getSyncStatus() {
    try {
      const stored = await AsyncStorage.getItem(CrossPortalDataManager.STORAGE_KEYS.CROSS_PORTAL_SYNC);
      return stored ? JSON.parse(stored) : {};
    } catch { return {}; }
  }

  async sendCrossPortalNotification(notification) {
    try {
      const stored = await AsyncStorage.getItem(CrossPortalDataManager.STORAGE_KEYS.NOTIFICATION_QUEUE);
      const queue = stored ? JSON.parse(stored) : [];
      queue.unshift({ ...notification, id: `notif_${Date.now()}`, timestamp: new Date().toISOString(), read: false });
      await AsyncStorage.setItem(CrossPortalDataManager.STORAGE_KEYS.NOTIFICATION_QUEUE, JSON.stringify(queue));
      return { success: true };
    } catch (error) { return { success: false, error: error.message }; }
  }

  async getPortalNotifications(portalType) {
    try {
      const stored = await AsyncStorage.getItem(CrossPortalDataManager.STORAGE_KEYS.NOTIFICATION_QUEUE);
      const all = stored ? JSON.parse(stored) : [];
      return portalType ? all.filter(n => n.targetPortal === portalType || !n.targetPortal) : all;
    } catch { return []; }
  }

  async updateApplicationStatus(applicationId, newStatus, newStage, notes = '') {
    try {
      // Update via API
      await apiCall('PUT', `/api/applications/${applicationId}`, { status: newStatus, stage: newStage, notes });
      // Also update local cache
      const stored = await AsyncStorage.getItem(CrossPortalDataManager.STORAGE_KEYS.JOB_APPLICATIONS);
      const apps = stored ? JSON.parse(stored) : [];
      const idx = apps.findIndex(a => String(a.id) === String(applicationId));
      if (idx >= 0) { apps[idx] = { ...apps[idx], status: newStatus, stage: newStage, notes, updatedAt: new Date().toISOString() }; }
      await AsyncStorage.setItem(CrossPortalDataManager.STORAGE_KEYS.JOB_APPLICATIONS, JSON.stringify(apps));
      await this.sendCrossPortalNotification({ type: 'status_update', title: 'Application Updated', body: `Status: ${newStatus}`, applicationId, targetPortal: 'professional' });
      return { success: true };
    } catch (error) { return { success: false, error: error.message }; }
  }

  async syncStatusToProfessionalPortal(candidate) {
    try {
      await this.sendCrossPortalNotification({ type: 'candidate_update', title: 'Application Update', body: `Your application status has been updated to: ${candidate.status}`, candidateId: candidate.id, targetPortal: 'professional' });
      return { success: true };
    } catch (error) { return { success: false, error: error.message }; }
  }

  async scheduleInterviewCrossPortal(interviewData) {
    try {
      // Create interview via API
      const res = await apiCall('POST', '/api/interviews', interviewData);
      await this.sendCrossPortalNotification({ type: 'interview_scheduled', title: 'Interview Scheduled', body: `Interview on ${interviewData.scheduled_date || interviewData.interview_date || 'TBD'}`, applicationId: interviewData.application_id, targetPortal: 'professional' });
      return { success: true, interview: res?.data || interviewData };
    } catch (error) { return { success: false, error: error.message }; }
  }

  async sendJobOfferCrossPortal(offerData) {
    try {
      const res = await apiCall('POST', '/api/offers', offerData);
      await this.sendCrossPortalNotification({ type: 'job_offer', title: 'Job Offer', body: `You have received a job offer for ${offerData.job_title || 'a position'}`, applicationId: offerData.application_id, targetPortal: 'professional' });
      return { success: true, offer: res?.data || offerData };
    } catch (error) { return { success: false, error: error.message }; }
  }

  async handleOfferResponse(offerId, response, professionalId) {
    try {
      await apiCall('PUT', `/api/offers/${offerId}`, { status: response, professional_id: professionalId });
      await this.sendCrossPortalNotification({ type: 'offer_response', title: `Offer ${response}`, body: `The candidate has ${response} the offer`, offerId, targetPortal: 'business' });
      return { success: true };
    } catch (error) { return { success: false, error: error.message }; }
  }

  async performFullSync() {
    try {
      const [jobsResult, appsResult] = await Promise.all([
        this.syncJobsToProPortal(), this.syncApplicationsToBizPortal(),
      ]);
      const overall = jobsResult.success && appsResult.success;
      await this.updateSyncStatus('fullSync', overall ? 'success' : 'partial', new Date().toISOString());
      return { success: overall, jobs: jobsResult, applications: appsResult, timestamp: new Date().toISOString() };
    } catch (error) { return { success: false, error: error.message }; }
  }
}

export const useCrossPortalSync = () => {
  const dataManager = new CrossPortalDataManager();
  return {
    syncJobs: async (jobs) => await dataManager.syncJobsToProPortal(jobs),
    syncApplications: async (applications) => await dataManager.syncApplicationsToBizPortal(applications),
    updateApplicationStatus: async (applicationId, status, stage, notes) => await dataManager.updateApplicationStatus(applicationId, status, stage, notes),
    scheduleInterview: async (interviewData) => await dataManager.scheduleInterviewCrossPortal(interviewData),
    sendJobOffer: async (offerData) => await dataManager.sendJobOfferCrossPortal(offerData),
    handleOfferResponse: async (offerId, response, professionalId) => await dataManager.handleOfferResponse(offerId, response, professionalId),
    performFullSync: async () => await dataManager.performFullSync(),
    getSyncStatus: async () => await dataManager.getSyncStatus(),
    getNotifications: async (portalType) => await dataManager.getPortalNotifications(portalType),
  };
};

export class CrossPortalEventEmitter {
  static listeners = {};
  static on(event, callback) { if (!this.listeners[event]) this.listeners[event] = []; this.listeners[event].push(callback); }
  static off(event, callback) { if (this.listeners[event]) this.listeners[event] = this.listeners[event].filter(cb => cb !== callback); }
  static emit(event, data) { if (this.listeners[event]) this.listeners[event].forEach(cb => cb(data)); }
}

export class AutoSyncService {
  static isRunning = false;
  static interval = null;
  static start(intervalMinutes = 5) {
    if (this.isRunning) return;
    this.isRunning = true;
    const dm = new CrossPortalDataManager();
    this.interval = setInterval(async () => {
      try { await dm.performFullSync(); CrossPortalEventEmitter.emit('sync_completed', { timestamp: new Date().toISOString() }); }
      catch (e) { CrossPortalEventEmitter.emit('sync_error', { error: e.message }); }
    }, intervalMinutes * 60 * 1000);
  }
  static stop() { if (this.interval) { clearInterval(this.interval); this.interval = null; } this.isRunning = false; }
}

export default { CrossPortalDataManager, useCrossPortalSync, CrossPortalEventEmitter, AutoSyncService };